import { FormControl, Grid, Typography } from "@mui/material";
import convert, { Unit } from "convert";
import { get, set } from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import { createDefaultVital } from ".";
import { BmiInputProps } from "../../../../../../components/widgets/modals/encounter";
import CodingSystems from "../../../../../../constants/CodingSystems";
import usePractitioner from "../../../../../../hooks/usePractitioner";
import { calculateBmiFeedback } from "../../../../../../utils/bmiUtils";
import { Feedback } from "../types/commonCDSTypes";
import {
  bmiCheckDisplay,
  getSimpleVitalValue,
  getWeightKg,
  updateMetaData,
} from "../VitalsFormHelper";
import CdsArrowTooltip from "./cdsArrowTooltip";
import CdsFeedbackTextField from "./CdsFeedbackTextField";
import VitalCdsUnitTypography from "./VitalCdsUnitTypography";

const BmiInput: FC<BmiInputProps> = ({
  encounter,
  patient,
  value,
  onChange,
  height,
  weight,
  disabled,
  size,
}) => {
  const practitionerReference = usePractitioner();

  const [bmi, setBmi] = useState<any>();
  const [bmiDisplay, setBmiDisplay] = useState<string>();
  const [feedback, setFeedback] = useState<Feedback>();
  const [showClassification, setShowClassification] = useState(false);
  const previousHeightValue = useRef<number | null>(null);
  const previousWeightValue = useRef<number | null>(null);

  const heightInCentimeters = (height: {
    valueQuantity: { value: number; unit: string };
  }) => {
    return convert(
      height?.valueQuantity?.value ?? 0,
      (height?.valueQuantity?.unit as Unit) ?? "cm",
    ).to("cm");
  };

  useEffect(() => {
    const newBmi = { ...bmi };
    const currentBmiValue = value?.valueQuantity?.value ?? null;
    const nextHeight = getSimpleVitalValue(height);
    const nextWeight = getSimpleVitalValue(weight);
    const heightEdited =
      previousHeightValue.current != null &&
      previousHeightValue.current !== nextHeight;
    const weightEdited =
      previousWeightValue.current != null &&
      previousWeightValue.current !== nextWeight;

    if (nextHeight != null) {
      previousHeightValue.current = nextHeight;
    }
    if (nextWeight != null) {
      previousWeightValue.current = nextWeight;
    }

    const emitIfChanged = (nextValue) => {
      if (String(nextValue ?? "") === String(currentBmiValue ?? "")) {
        return;
      }
      updateMetaData(newBmi, practitionerReference);
      onChange(newBmi);
    };

    if (
      currentBmiValue != null &&
      currentBmiValue !== "" &&
      !heightEdited &&
      !weightEdited
    ) {
      setBmiDisplay(String(currentBmiValue));
      setFeedback(calculateBmiFeedback(Number(currentBmiValue)));
      return;
    }

    // Check if height and weight values exist
    if (!height?.valueQuantity?.value || !weight?.valueQuantity?.value) {
      setBmiDisplay("-");
      set(newBmi, "valueQuantity.value", null);
      setFeedback(undefined);
      setShowClassification(false);
      emitIfChanged(null);
      return;
    }

    const ht = heightInCentimeters(height);
    const wt =
      weight?.valueQuantity?.unit === "kg"
        ? weight.valueQuantity.value
        : getWeightKg(weight);

    let bodyMassIndex;
    if (ht === 0 || ht === null || wt === null) {
      setBmiDisplay("-");
      bodyMassIndex = null;
      setFeedback(undefined);
      setShowClassification(false);
    } else {
      bodyMassIndex = (wt / ((ht / 100) * (ht / 100))).toFixed(1);
      if (Number.isNaN(bodyMassIndex)) {
        setBmiDisplay(null);
        bodyMassIndex = null;
        setFeedback(undefined);
        setShowClassification(false);
      } else {
        setBmiDisplay(bodyMassIndex);
        // Update feedback based on BMI value
        setFeedback(calculateBmiFeedback(Number(bodyMassIndex)));
        // Show classification for 2.5 seconds
        setShowClassification(true);
        setTimeout(() => {
          setShowClassification(false);
        }, 2500);
      }
    }

    const nextValue = bodyMassIndex ? bodyMassIndex : null;
    set(newBmi, "valueQuantity.value", nextValue);
    emitIfChanged(nextValue);
  }, [height, weight, value]);

  useEffect(() => {
    if (value) {
      setBmi(value);
      if (value?.valueQuantity?.value) {
        setFeedback(calculateBmiFeedback(Number(value.valueQuantity.value)));
        // Show classification for 2.5 seconds when value changes
        setShowClassification(true);
        setTimeout(() => {
          setShowClassification(false);
        }, 2500);
      }
    } else {
      const bmi = createDefaultVital(
        encounter,
        patient,
        CodingSystems.OBSERVATION_CODE,
        "39156-5",
        "Body Mass Index",
        "kg/m2",
      );
      setBmi(bmi);
      setFeedback(undefined);
      setShowClassification(false);
    }
  }, [encounter, patient, value]);

  const bmiNum = Number(bmiDisplay);

  // if (!bmiDisplay || bmiNum === 0) {
  //   return null;
  // }

  return (
    <Grid item xs={12} position="relative">
      <FormControl fullWidth>
        {bmiDisplay && bmiNum > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: bmiCheckDisplay(bmiDisplay).bmiDisplayColour,
              fontWeight: "bold",
              opacity: showClassification ? 1 : 0,
              transition: "opacity 0.5s ease-in-out",
              height: showClassification ? "auto" : 0,
              overflow: "hidden",
              position: "absolute",
              bottom: "100%",
              left: "50%",
              transform: "translate(-50%, 0%)",
              zIndex: 1,
            }}
          >
            {bmiCheckDisplay(bmiDisplay).bmiClassification}
          </Typography>
        )}
        <CdsArrowTooltip
          type={feedback?.type}
          feedbackText={feedback?.message}
          openCDS={bmiNum >= 1}
        >
          <CdsFeedbackTextField
            feedbackType={getSimpleVitalValue(bmi) ? feedback?.type : undefined}
            value={getSimpleVitalValue(bmi) ?? ""}
            disabled={disabled}
            onChange={() => {}} // BMI is calculated automatically, no direct input
            id="vitals-bmi"
            label="BMI"
            inputMode="numeric"
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <VitalCdsUnitTypography
                  disabled={disabled}
                  feedbackType={
                    getSimpleVitalValue(bmi) ? feedback?.type : undefined
                  }
                >
                  {get(bmi, "valueQuantity.unit")}
                </VitalCdsUnitTypography>
              ),
            }}
            variant="outlined"
            size={size || "medium"}
            type="text"
            sx={{
              "& .MuiInputBase-input": {
                cursor: "not-allowed",
                borderRadius: "8px",
                // color: "#777", //disable color
              },
            }}
          />
        </CdsArrowTooltip>
      </FormControl>
    </Grid>
  );
};

export default BmiInput;
